<template>
  <div class="content">
    <div class="md-layout">
      <h3
        v-if="editMode"
        class="md-headline h2"
      >
        Edit Product
      </h3>
      <h3
        v-else
        class="md-headline h2"
      >
        Create Product
      </h3>
      <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
        <!-- Product form -->
        <md-card>
          <md-card-content>
            <form class="node-form">
              <md-field
                class="field"
                :class="$v.localization.name.$error && 'md-invalid invalid-field'"
              >
                <label>Name</label>
                <md-input
                  v-model="localization.name"
                  :disabled="loading"
                />
                <span class="md-error">Field must not be empty</span>
              </md-field>

              <md-field
                class="field"
                :class="$v.product.sku.$error && 'md-invalid invalid-field'"
              >
                <label>SKU</label>
                <md-input
                  v-model="product.sku"
                  :disabled="loading"
                />
                <span class="md-error">
                  {{
                    !$v.product.sku.isRenamedAfterCopy
                      ? 'Should it end with ".COPY"?'
                      : 'Field is required'
                  }}
                </span>
              </md-field>

              <md-field
                class="field"
                :class="$v.product.index.$error && 'md-invalid invalid-field'"
              >
                <label>Index</label>
                <md-input
                  v-model="product.index"
                  :disabled="loading"
                />
                <span class="md-error">Field must be an integer</span>
              </md-field>

              <div class="divider" />

              <md-switch
                v-model="product.enabled"
                :disabled="loading"
              >
                Enabled
              </md-switch>

              <md-field class="field">
                <label>Version Filter Pattern</label>
                <md-input
                  v-model="product.version_filter_pattern"
                  :disabled="loading"
                />
                <span class="md-helper-text">Possible values: &lt;1.1.1 more than, >1.1.1 less than, 1.1.1 equal, empty - no filter</span>
              </md-field>

              <div class="divider" />

              <div class="product-type">
                <span class="md-caption">Product type</span>
                <br>
                <md-radio
                  v-model="product.product_type"
                  value="subscription"
                  :disabled="loading"
                >
                  Subscription
                </md-radio>
                <md-radio
                  v-model="product.product_type"
                  value="one_time_purchase"
                  :disabled="loading"
                >
                  One time purchase
                </md-radio>
              </div>

              <div class="trial-field-line">
                <md-switch
                  v-model="product.has_trial"
                  :disabled="loading"
                >
                  Has trial?
                </md-switch>
                <md-field :class="$v.product.trial_period_in_days.$error && 'md-invalid invalid-field'">
                  <label>Trial period, days</label>
                  <md-input
                    v-model="product.trial_period_in_days"
                    :disabled="loading || !product.has_trial"
                  />
                  <span class="md-error">Field must be an integer</span>
                </md-field>
              </div>

              <div class="price-field-line">
                <md-field :class="$v.localization.amount.$error && 'md-invalid invalid-field'">
                  <label>Amount</label>
                  <md-input
                    v-model="localization.amount"
                    type="integer"
                    :disabled="loading"
                  />
                  <span class="md-error">Field must be an integer</span>
                </md-field>

                <md-autocomplete
                  v-model="localization.currency"
                  :md-options="currencyCodes.map(c => c.name)"
                  :class="$v.localization.currency.$error && 'md-invalid invalid-field'"
                  :disabled="loading"
                  @md-selected="onCurrencyPicked"
                >
                  <label>Currency</label>
                  <span class="md-error">Field is required</span>
                </md-autocomplete>
              </div>

              <div class="price-field-line">
                <md-field
                  :class="$v.product.credit_to_apply_on_wallet.$error && 'md-invalid invalid-field'"
                >
                  <label>Credit to apply on wallet</label>
                  <md-input
                    v-model="product.credit_to_apply_on_wallet"
                    type="number"
                    :disabled="loading"
                  />
                  <span class="md-error">Field must be an integer</span>
                </md-field>
              </div>

              <md-switch
                v-model="localization.best_price"
                :disabled="loading"
              >
                Best Value Label
              </md-switch>

              <div class="price-field-line">
                <md-field :class="$v.localization.duration.$error && 'md-invalid invalid-field'">
                  <label>Duration</label>
                  <md-input
                    v-model="localization.duration"
                    :disabled="loading"
                    @change="onDurationChange"
                  />
                  <span class="md-suffix">hours</span>
                  <span class="md-helper-text">{{ durationHelperText }}</span>
                  <span class="md-error">Field must be an integer</span>
                </md-field>
              </div>

              <div class="price-field-line">
                <md-field
                  :class="$v.localization.data.discount.en.$error && 'md-invalid invalid-field'"
                >
                  <label>Discount</label>
                  <md-input
                    v-model="localization.data.discount.en"
                    :disabled="loading"
                  />
                  <span class="md-suffix">%</span>
                  <span class="md-error">Field must be an integer</span>
                </md-field>
              </div>
            </form>

            <country-selector-with-continents
              :initial-mode="product['countries_access_type']"
              :initial-iso-codes="product['countries_iso_codes']"
              with-copy-paste="true"
              @countryListChanged="onCountriesListChanged"
              @modeChanged="onCountriesModeChanged"
            />

            <div class="divider" />

            <div class="payment-provider">
              <span class="h3">Payment provider</span>
              <br>
              <md-radio
                v-model="paymentProvider"
                :disabled="loading"
                value="chargebee"
              >
                Chargebee
              </md-radio>
              <md-radio
                v-model="paymentProvider"
                :disabled="loading"
                value="platform"
              >
                Platform
              </md-radio>
              <md-radio
                v-model="paymentProvider"
                :disabled="loading"
                value="stripe"
              >
                Stripe
              </md-radio>
              <md-radio
                v-model="paymentProvider"
                :disabled="loading"
                value="none"
              >
                None
              </md-radio>
            </div>

            <!-- Chargebee form -->
            <div v-if="paymentProvider === 'chargebee'">
              <md-field :class="$v.chargebee.chargebee_product_id.$error && 'md-invalid invalid-field'">
                <label>Chargebee Product ID</label>
                <md-input
                  v-model="chargebee.chargebee_product_id"
                  :disabled="loading"
                />
                <span class="md-error">Field is required</span>
              </md-field>

              <md-field :class="$v.chargebee.name.$error && 'md-invalid invalid-field'">
                <label>Chargebee Product Name</label>
                <md-input
                  v-model="chargebee.name"
                  :disabled="loading"
                />
                <span class="md-error">Field is required</span>
              </md-field>

              <!--                        <md-field :class="$v.chargebee.description.$error && 'md-invalid invalid-field'">-->
              <md-field>
                <label>Description</label>
                <md-textarea
                  v-model="chargebee.description"
                  :disabled="loading"
                />
                <!--                          <span class="md-error">Required field</span>-->
              </md-field>
            </div>
            <!-- Platform form -->
            <div v-else-if="paymentProvider === 'platform'">
              <md-field
                :class="$v.platform.platform_payment_product_id.$error && 'md-invalid invalid-field'"
              >
                <label>Platform Product ID</label>
                <md-input
                  v-model="platform.platform_payment_product_id"
                  :disabled="loading"
                />
                <span class="md-error">Field is required</span>
              </md-field>

              <md-field :class="$v.platform.name.$error && 'md-invalid invalid-field'">
                <label>Platform Product Name</label>
                <md-input
                  v-model="platform.name"
                  :disabled="loading"
                />
                <span class="md-error">Field is required</span>
              </md-field>

              <!--                        <md-field :class="$v.chargebee.description.$error && 'md-invalid invalid-field'">-->
              <md-field>
                <label>Description</label>
                <md-textarea
                  v-model="platform.description"
                  :disabled="loading"
                />
                <!--                          <span class="md-error">Required field</span>-->
              </md-field>
            </div>
            <!-- Stripe form -->
            <div v-else-if="paymentProvider === 'stripe'">
              <md-field :class="$v.stripe.stripe_product_id.$invalid && 'md-invalid invalid-field'">
                <label>Stripe Product ID</label>
                <md-input
                  v-model="stripe.stripe_product_id"
                  :disabled="loading"
                />
                <span class="md-error">Field is required</span>
              </md-field>
              <div>
                <md-radio
                  v-model="stripe.product_type"
                  value="subscription"
                  class="md-primary"
                  :disabled="loading"
                >
                  Subscription
                </md-radio>
                <md-radio
                  v-model="stripe.product_type"
                  value="one_time_purchase"
                  class="md-primary"
                  :disabled="loading"
                >
                  One-time purchase
                </md-radio>
              </div>
              <md-field :class="$v.stripe.trial_days.$error && 'md-invalid invalid-field'">
                <label>Trial days</label>
                <md-input
                  v-model="stripe.trial_days"
                  :disabled="loading"
                />
                <span class="md-error">Must be a number</span>
              </md-field>
            </div>
          </md-card-content>

          <md-card-actions>
            <md-button
              v-if="!editMode"
              :disabled="loading"
              type="submit"
              @click.native="submitProductForm(true)"
            >
              <span v-if="!loading">Save and create new</span>
              <md-progress-spinner
                v-else
                :md-diameter="15"
                :md-stroke="3"
                md-mode="indeterminate"
              />
            </md-button>

            <md-button
              :disabled="loading"
              type="submit"
              class="md-primary"
              @click.native="submitProductForm(false)"
            >
              <span v-if="!loading">Save</span>
              <md-progress-spinner
                v-else
                :md-diameter="15"
                :md-stroke="3"
                md-mode="indeterminate"
              />
            </md-button>
          </md-card-actions>
        </md-card>

        <div
          v-if="editMode"
          class="old-version-wrapper"
        >
          <div class="old-version-info">
            <md-icon>info</md-icon>
            <p>
              Missing some fields or good old days?
              <br>
              Get back to the old <a :href="$route.path.slice(0, -5)">version</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {required, integer, requiredIf} from "vuelidate/lib/validators";
import CountrySelectorWithContinents from "@/components/Forms/Fields/CountrySelectorWithContinents";
import {currencyCodes} from "../assets/countries_iso/currency-codes";

export default {
    components: {
        CountrySelectorWithContinents
    },
    data() {
        return {
            product: {
                "app_id": this.$route.params.appid,
                sku: "",
                index: "",
                product_type: "subscription",
                enabled: false,
                has_trial: false,
                trial_period_in_days: 0,
                "countries_access_type": "common",
                "countries_iso_codes": [],
                version_filter_pattern: "",
                credit_to_apply_on_wallet: "0",
                data: {
                    // may not be needed
                    title: {},
                    subtitle: {},
                    cta: {}
                },
                benefits: []
            },

            localization: {
                name: "",
                amount: "",
                currency: "",
                duration: "",
                best_price: false,

                data: {
                    discount: {
                        en: ""
                    }
                }
            },

            chargebee: {
                chargebee_product_id: "",
                name: "",
                description: "",
            },
            platform: {
                platform_payment_product_id: "",
                name: "",
                description: ""
            },
            stripe: {
                product_type: "subscription",
                stripe_product_id: "",
                trial_days: ""
            },

            editMode: false,
            loading: false,
            currencyCodes,
            durationHelperText: "",
            paymentProvider: "chargebee",
            fetchedPaymentProviders: {}
        };
    },
    watch: {
      "localization.name": function (val) {
        this.product.data.title.en = val;
      }
    },
    mounted() {
        if (location.pathname.includes("/edit")) {
            this.editMode = true;
            document.title = "Edit Product";
            this.fetchProduct()
                .then(() => {
                    document.title = `${this.product.sku} - Edit Product`;
                    this.loading = false;
                });
        } else if (location.pathname.includes("/clone")) {
            document.title = "Clone Product";
            this.fetchProduct()
                .then(() => {
                    document.title = `${this.product.sku} - Clone Product`;
                    this.product.sku += ".COPY";

                    delete this.product.id;
                    delete this.product.name;
                    delete this.product.app_identity;
                    this.product.app_id = this.$route.params.appid;

                    delete this.localization.id;
                    delete this.localization.product_id;

                    this.loading = false;
                });
        }
    },
    methods: {
        onCountriesModeChanged(mode) {
            this.product["countries_access_type"] = mode;
        },
        onCountriesListChanged(newList) {
            this.product["countries_iso_codes"] = newList;
        },
        onCurrencyPicked(currencyName) {
            this.localization.currency = this.currencyCodes.find(c => c.name === currencyName).code;
        },
        onDurationChange() {
            const days = this.localization.duration / 24;

            if (days < 1) {
                this.durationHelperText = "";
                return;
            }

            const years = (days / 365).toFixed(2);
            const months = (days / 30).toFixed(2);
            const weeks = (days / 7).toFixed(2);

            if (years >= 1) {
                this.durationHelperText = `≈${years} years`;
            } else if (months >= 1) {
                this.durationHelperText = `≈${months} months`;
            } else if (weeks >= 1) {
                this.durationHelperText = `≈${weeks} months`;
            } else {
                this.durationHelperText = `≈${days} days`;
            }
        },
        fetchProduct() {
            this.loading = true;

            const productId = this.$route.params.id;
            return this.$http.get(`/v1/admin/store/product/${productId}`)
                .then(result => (this.product = result.data.product))
                .then(() => this.$http.get(`/v1/admin/store/product-localizations/${productId}`))
                .then((res) => (this.localization = res.data.product_localizations.find(l => l.default_localization)))
                .then(() => Promise.all([
                    this.$http.get(`/v1/admin/store/chargebee-integration/${productId}`)
                        .then(res => res, err => ({ err })),
                    this.$http.get(`/v1/admin/store/platform-payment-integration/${productId}`)
                        .then(res => res, err => ({ err })),
                    this.$http.get(`/v1/admin/store/stripe-integration/${productId}`)
                        .then(res => res, err => ({ err })),
                ]))
                .then((res) => {
                    const existingPaymentMethods = res.filter(req => !req.err);
                    // if all payment methods failed
                    if (existingPaymentMethods.length === 0) {
                        this.fetchedPaymentProviders = {};
                        this.paymentProvider = "none";
                        return;
                    }

                    existingPaymentMethods.forEach(({ data: paymentData }) => {
                        if (paymentData.chargebee_product_id) {
                            this.fetchedPaymentProviders.chargebee = true;
                            this.paymentProvider = "chargebee";
                            this.chargebee = paymentData;
                        } else if (paymentData.platform_payment_product_id) {
                            this.fetchedPaymentProviders.platform = true;
                            this.paymentProvider = "platform";
                            this.platform = paymentData;
                        } else if (paymentData.stripe_product_id) {
                            this.fetchedPaymentProviders.stripe = true;
                            this.paymentProvider = "stripe";
                            this.stripe = paymentData;
                        }
                    });
                })
                .then(() => {
                    this.onDurationChange();
                });
        },
        submitProductForm(createNew = false) {
            this.$v.$touch();

            if (this.$v.$error) {
                return;
            }

            if (this.editMode) {
                this.updateProduct();
            } else {
                this.createProduct(createNew);
            }
        },
        createProduct(createNew) {
            this.loading = true;

            /*
              The form above has fields from two entities: `Product` and `Product Localization`.
              To properly save the data, the following steps are required:
                1. POST /product/create -> to create a Product
                2. From the above response, pull out the `id` of the newly created Product
                3. GET /product-localizations -> to get the localization(s) that were created alongside the Product
                4. Find the Localization with `default_localization`
                5. Spread the form data saved in `localization` over the `default_localization` (just to make sure we're not dropping anything)
                6. PUT /v1/admin/store/product-localization -> to save changes
             */
            let productId;
            this.$http.post("/v1/admin/store/product/create", this.product)
                .then(res => {
                    productId = res.data.id;
                    return this.$http.get(`/v1/admin/store/product-localizations/${res.data.id}`);
                })
                .then(res => res.data.product_localizations.find(l => l.default_localization))
                .then(local => {
                    Object.entries(this.localization).forEach(([key, value]) => {
                        if (typeof value !== "object" || Array.isArray(value)) {
                            local[key] = value;
                        } else {
                            if (local[key] && typeof local[key] === "object") {
                                local[key] = {...local[key], ...this.localization[key]};
                            } else {
                                local[key] = this.localization[key];
                            }
                        }
                    });

                    return this.$http.put("/v1/admin/store/product-localization", {"product_localization": local});
                })
                .then(() => {
                    if (this.paymentProvider === "stripe") {
                        return this.$http.post("/v1/admin/store/stripe-integration", {
                            ...this.stripe,
                            product_id: productId,
                        });
                    } else if (this.paymentProvider === "chargebee") {
                        return this.$http.post("/v1/admin/store/chargebee-integration", {
                            ...this.chargebee,
                            product_id: productId,
                        });
                    } else if (this.paymentProvider === "platform") {
                        return this.$http.post("/v1/admin/store/platform-payment-integration", {
                            ...this.platform,
                            product_id: this.productId
                        });
                    } else if (this.paymentProvider === "none") {
                        return Promise.resolve();
                    } else {
                        throw new Error("Unknown payment provider selected");
                    }
                })
                .then(() => {
                    this.$notify({
                        group: "foo",
                        title: "Product created",
                    });
                    if (createNew) {
                        window.location.reload();
                    } else {
                        window.location.href = `/app/${this.$route.params.appid}/product/${productId}/edit`;
                    }
                })
                .catch(err => {
                    console.error(err);
                    this.$notify({
                        group: "foo",
                        title: err,
                    });
                    this.loading = false;
                });
        },
        updateProduct() {
            this.loading = true;

            this.$http.post("/v1/admin/store/product/update", this.product)
                .then(() => this.$http.put("/v1/admin/store/product-localization", {"product_localization": this.localization}))
                .then(() => {
                    const productId = this.$route.params.id;

                    // if the payment provider being saved is not the one, that was initially fetched,
                    // we'll need to `POST` it, instead of `PUT` 'ting it.
                    const paymentProviderSaveMethod = this.fetchedPaymentProviders[this.paymentProvider] ? "put" : "post";

                    if (this.paymentProvider === "stripe") {
                        return this.$http[paymentProviderSaveMethod]("/v1/admin/store/stripe-integration", {
                            ...this.stripe,
                            product_id: productId,
                        });
                    } else if (this.paymentProvider === "chargebee") {
                        return this.$http[paymentProviderSaveMethod]("/v1/admin/store/chargebee-integration", {
                            ...this.chargebee,
                            product_id: productId,
                        });
                    } else if (this.paymentProvider === "platform") {
                        return this.$http[paymentProviderSaveMethod]("/v1/admin/store/platform-payment-integration", {
                            ...this.platform,
                            product_id: productId
                        });
                    } else if (this.paymentProvider === "none") {
                      return Promise.resolve();
                    } else {
                        throw new Error("Unknown payment provider selected");
                    }
                })
                .then(() => {
                    this.$notify({
                        group: "foo",
                        title: "Product saved"
                    });
                    this.loading = false;
                })
                .catch(err => {
                    console.error(err);
                    this.$notify({
                        group: "foo",
                        title: err,
                    });
                    this.loading = false;
                });
        }
    },
    validations: {
        product: {
            sku: {
                required,
                isRenamedAfterCopy (sku) {
                    return !sku.endsWith(".COPY");
                }
            },
            index: {required, integer},
            "countries_iso_codes": {
                required: requiredIf(function () {
                    return this.product["countries_access_type"] !== "common";
                })
            },
            trial_period_in_days: {
                required: requiredIf(function () {
                    return this.product.has_trial;
                }),
                integer
            },
            credit_to_apply_on_wallet: {integer}
        },
        localization: {
            name: {required},
            amount: {required, integer},
            currency: {required},
            duration: {
                required: requiredIf(function () {
                    return this.product.product_type === "subscription";
                }), integer
            },

            data: {
                discount: {
                    en: {integer}
                }
            }
        },
        chargebee: {
            chargebee_product_id: {
                required: requiredIf(function () {
                    return this.paymentProvider === "chargebee";
                })
            },
            name: {
                required: requiredIf(function () {
                    return this.paymentProvider === "chargebee";
                })
            },
        },
        platform: {
            platform_payment_product_id: {
                required: requiredIf(function () {
                    return this.paymentProvider === "platform";
                })
            },
            name: {
                required: requiredIf(function () {
                    return this.paymentProvider === "platform";
                })
            },
        },
        stripe: {
            product_type: {
                required: requiredIf(function () {
                    return this.paymentProvider === "stripe";
                })
            },
            stripe_product_id: {
                required: requiredIf(function () {
                    return this.paymentProvider === "stripe";
                })
            },
            trial_days: {
                required: requiredIf(function () {
                    return this.paymentProvider === "stripe";
                }), integer
            },
        }
    }
};
</script>
<style scoped lang="scss">
.invalid-field {
    color: #ff1745;

    input, label {
        color: #ff1745 !important;
    }
}

.invalid-field:before {
    background-color: #ff1745 !important;
}

.product-type {
    margin-bottom: 20px;
}

.trial-field-line {
    display: flex;

    & > div {
        width: 150px;
    }
}

.enabled-field-line {
    display: flex;

    & > div:last-child {
        width: 450px;
    }
}

.price-field-line {
    display: grid;
    grid-template-columns: 200px 200px;
    grid-column-gap: 12px;
}

.field {
    max-width: 450px;
}

.divider {
    margin: 60px 0 40px;
}

.old-version-wrapper {
    display: flex;
    justify-content: flex-end;

    .old-version-info {
        display: grid;
        grid-template-columns: 30px 250px;
        grid-column-gap: 16px;
        justify-content: end;

        padding: 8px;
        background: cornsilk;
        box-shadow: 2px 2px 2px gray;

        p {
            margin: 0;
        }
    }
}
</style>
